import { Loader2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useGenerateVideoScript } from '@/features/videos/api/generate-video-script';
import { usePatchVideoDraftConfig } from '@/features/videos/api/patch-video-draft-config';

export function ScriptEditorDialog({
  open,
  onOpenChange,
  videoId,
  templateId,
  variationId,
  serviceId,
  narrationMode,
  scriptText,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  templateId: string | null;
  variationId: string | null;
  serviceId: string | null;
  narrationMode: 'recorded' | 'ai_voiceover' | 'text_only' | null;
  scriptText: string;
  onSaved?: (scriptText: string, videoId: string) => void;
}) {
  const [value, setValue] = useState(scriptText);
  const { patchDraftConfigAsync, isPatching } = usePatchVideoDraftConfig();
  const { generateScriptAsync, isGenerating } = useGenerateVideoScript();
  const busy = isPatching || isGenerating;

  useEffect(() => {
    if (open) setValue(scriptText);
  }, [open, scriptText]);

  const save = async () => {
    try {
      const result = await patchDraftConfigAsync({
        videoId,
        patch: { scriptText: value.trim() },
        requeueRender: false,
      });
      toast.success('Script saved');
      onOpenChange(false);
      onSaved?.(value.trim(), result.video.id);
    } catch (error) {
      // Keep both the dialog and the owner's text in place for a retry.
      toast.error(
        error instanceof Error ? error.message : 'Failed to save script'
      );
    }
  };

  const rewriteWithAi = async () => {
    if (!templateId || !variationId) {
      toast.error('This video is missing its script template');
      return;
    }

    try {
      const refinementInstruction = variationId.startsWith('authority-')
        ? 'Rewrite this as a substantial 35-45 second authority script, roughly 85-110 words. Make it clear, engaging, and natural; cover the viewer problem, how the service works, realistic outcomes, credibility, and one consultation call to action. Preserve all factual claims.'
        : 'Rewrite this script to be clearer, more engaging, natural, and concise. Preserve its core message and factual claims.';
      const result = await generateScriptAsync({
        templateId,
        variationId,
        serviceId: serviceId ?? undefined,
        narrationMode: narrationMode ?? undefined,
        priorScriptText: value.trim(),
        refinementInstruction,
      });
      setValue(result.scriptText.trim());
      toast.success('Script rewritten — review and save');
    } catch (error) {
      // Replace nothing on failure: the owner's current draft remains editable.
      toast.error(
        error instanceof Error ? error.message : 'Failed to rewrite script'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit script</DialogTitle>
          <DialogDescription>
            This script is saved to the current video. Recording can be added
            afterwards.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={12}
          disabled={busy}
          placeholder="Add the narration script"
        />
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => void rewriteWithAi()}
            disabled={busy || !value.trim() || !templateId || !variationId}
          >
            {isGenerating ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {isGenerating ? 'Writing…' : 'Write with AI'}
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void save()}
              disabled={busy || !value.trim()}
            >
              {isPatching ? <Loader2 className="size-4 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
